import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FiSave, FiRotateCcw } from 'react-icons/fi';
import { appSettingsAPI } from '../../utils/api';
import '../styles/SystemSettings.css';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    appName: 'DISHA Dashboard',
    appVersion: '1.0.0',
    maxFileSize: 100,
    maxUsers: 1000,
    sessionTimeout: 30,
    enableEmailNotifications: true,
    enableTwoFactor: false,
    maintenanceMode: false,
    debugMode: false
  });

  // Only Session Timeout is backed by a real setting today — the other
  // fields above are UI scaffolding with nowhere to persist to yet.
  const [sessionTimeoutLoading, setSessionTimeoutLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await appSettingsAPI.getSettings();
        const minutes = Number(res?.data?.sessionTimeoutMinutes);
        if (!cancelled && Number.isFinite(minutes)) {
          setSettings((prev) => ({ ...prev, sessionTimeout: minutes }));
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load current session timeout.');
      } finally {
        if (!cancelled) setSessionTimeoutLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSaved('');
  };

  const handleSave = async () => {
    setError('');
    setSaved('');
    // Only Session Timeout actually persists — see comment above. The other
    // fields on this page (App Name, Max Users, Maintenance Mode, etc.)
    // aren't wired to anything server-side yet, so Save must not claim to
    // have stored them.
    const minutes = Number(settings.sessionTimeout);
    if (!Number.isInteger(minutes) || minutes < 5 || minutes > 1440) {
      setError('Session Timeout must be a whole number between 5 and 1440 minutes.');
      return;
    }
    setSaving(true);
    try {
      const res = await appSettingsAPI.updateSettings({ sessionTimeoutMinutes: minutes });
      setSaved(res?.message || `Session timeout updated to ${minutes} minute(s).`);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save session timeout.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    globalThis.location.reload();
  };

  return (
    <Container fluid className="system-settings">
      {saved && (
        <Alert variant="success" dismissible onClose={() => setSaved('')}>
          ✓ {saved}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* General Settings */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">General Settings</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Application Name</Form.Label>
                <Form.Control
                  type="text"
                  value={settings.appName}
                  onChange={(e) => handleSettingChange('appName', e.target.value)}
                  className="form-input"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Application Version</Form.Label>
                <Form.Control
                  type="text"
                  value={settings.appVersion}
                  disabled
                  className="form-input"
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* File & Storage Settings */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">File & Storage Settings</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maximum File Size (MB)</Form.Label>
                <Form.Control
                  type="number"
                  value={settings.maxFileSize}
                  onChange={(e) => handleSettingChange('maxFileSize', parseInt(e.target.value))}
                  className="form-input"
                />
                <small className="text-muted">Maximum allowed file upload size</small>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* User Settings */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">User Settings</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maximum Users</Form.Label>
                <Form.Control
                  type="number"
                  value={settings.maxUsers}
                  onChange={(e) => handleSettingChange('maxUsers', parseInt(e.target.value))}
                  className="form-input"
                />
                <small className="text-muted">Maximum allowed active users</small>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="d-flex align-items-center gap-2">
                  Session Timeout (minutes)
                  {sessionTimeoutLoading && <Spinner animation="border" size="sm" />}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={5}
                  max={1440}
                  value={settings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value, 10))}
                  disabled={sessionTimeoutLoading}
                  className="form-input"
                />
                <small className="text-muted">
                  How long a login lasts before you must sign in again (5–1440 min). Applies to new logins only —
                  anyone already signed in keeps their current session until they log in again.
                </small>
              </Form.Group>
            </Col>
          </Row>
          <small className="text-muted d-block mt-1">
            Maximum Users above is not wired up yet — only Session Timeout is a real, saved setting on this page for now.
          </small>
        </Card.Body>
      </Card>

      {/* Security Settings */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">Security Settings</h5>
        </Card.Header>
        <Card.Body>
          <Form.Check
            type="checkbox"
            label="Enable Two-Factor Authentication"
            checked={settings.enableTwoFactor}
            onChange={(e) => handleSettingChange('enableTwoFactor', e.target.checked)}
            className="mb-3"
          />
          <Form.Check
            type="checkbox"
            label="Enable Email Notifications"
            checked={settings.enableEmailNotifications}
            onChange={(e) => handleSettingChange('enableEmailNotifications', e.target.checked)}
            className="mb-3"
          />
        </Card.Body>
      </Card>

      {/* System Settings */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">System Settings</h5>
        </Card.Header>
        <Card.Body>
          <Form.Check
            type="checkbox"
            label="Maintenance Mode"
            checked={settings.maintenanceMode}
            onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
            className="mb-3"
          />
          <Form.Check
            type="checkbox"
            label="Debug Mode"
            checked={settings.debugMode}
            onChange={(e) => handleSettingChange('debugMode', e.target.checked)}
          />
          <small className="text-muted d-block mt-2">Debug mode will show detailed error messages</small>
        </Card.Body>
      </Card>

      {/* Actions */}
      <div className="settings-actions">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSave}
          disabled={saving || sessionTimeoutLoading}
          className="me-2"
        >
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FiSave className="me-2" />}
          {saving ? 'Saving…' : 'Save Session Timeout'}
        </Button>
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={handleReset}
        >
          <FiRotateCcw className="me-2" />
          Reset
        </Button>
      </div>
    </Container>
  );
};

export default SystemSettings;
